import React from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Avatar,
  alpha,
  useTheme,
  Chip,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ModernCard from './shared/ModernCard';

const MobileLeaderboard = ({
  leaderboardData,
  loading,
  error,
  onInviteFriend,
  currentUserId
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const LeaderboardHeader = () => (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        borderRadius: 2,
        p: 2,
        mb: 2,
      }}
    >
      <Stack spacing={1}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Leaderboard
        </Typography>
        <Typography variant="body2" sx={{ color: alpha(theme.palette.primary.main, 0.9) }}>
          Compete with friends in bank bonus hunting
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={onInviteFriend}
          fullWidth
          sx={{
            mt: 1,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            borderRadius: 1.5,
            textTransform: 'none',
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            },
          }}
        >
          Invite Friend
        </Button>
      </Stack>
    </Box>
  );

  const LeaderboardContent = () => {
    if (loading) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography>Loading leaderboard...</Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error">{error}</Typography>
        </Box>
      );
    }

    if (leaderboardData.length === 0) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography>Invite friends to start competing!</Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={1}>
        {leaderboardData.map((user) => (
          <ModernCard
            key={user.id}
            highlight={user.id === currentUserId}
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: 'none',
              ...(user.id === currentUserId && {
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              }),
            }}
          >
            <Stack spacing={1.5}>
              {/* Top Row: Rank and User Info */}
              <Stack direction="row" alignItems="center" spacing={2}>
                <Chip
                  label={`#${user.rank}`}
                  size="small"
                  sx={{
                    backgroundColor: user.rank <= 3 
                      ? alpha(theme.palette.primary.main, 0.08)
                      : alpha(theme.palette.text.secondary, 0.08),
                    color: user.rank <= 3 
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                    fontWeight: 600,
                    minWidth: 40,
                  }}
                />
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: user.isFriend
                        ? alpha(theme.palette.success.main, 0.08)
                        : alpha(theme.palette.primary.main, 0.08),
                      color: user.isFriend
                        ? theme.palette.success.main
                        : theme.palette.primary.main,
                    }}
                  >
                    {user.name.charAt(0)}
                  </Avatar>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {user.id === currentUserId ? 'You' : user.name}
                    </Typography>
                    {user.isFriend && (
                      <Chip
                        label="Friend"
                        size="small"
                        sx={{
                          height: 20,
                          backgroundColor: alpha(theme.palette.success.main, 0.08),
                          color: theme.palette.success.main,
                          '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
                        }}
                      />
                    )}
                  </Stack>
                </Stack>
              </Stack>

              {/* Bottom Row: Stats */}
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{
                  pt: 1,
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                }}
              >
                <Stack alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    Points
                  </Typography>
                  <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                    {user.points.toLocaleString()}
                  </Typography>
                </Stack>
                <Stack alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    Bonuses
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {user.bonusesClaimed}
                  </Typography>
                </Stack>
                <Stack alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    Earned
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    ${user.totalEarned.toLocaleString()}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </ModernCard>
        ))}
      </Stack>
    );
  };

  return (
    <Stack spacing={0} sx={{ width: '100%', pb: 2, mt: '56px' }}>
      <LeaderboardHeader />
      <LeaderboardContent />
    </Stack>
  );
};

export default MobileLeaderboard; 